import { Navigate } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import PageHeading from "@/components/PageHeading/PageHeading";
import Tabs from "@/components/Tabs";
import { useCanViewCommitteesBool } from "@/hooks/useCapabilities";
import { useParams } from "@/hooks/useSearchParams";
import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import type { Path } from "@/router";
import { getMembershipServicesNavLinks } from "../../../_membershipServicesNavLinks";
import { useFlags } from "@/hooks/useFeatureFlags";
import { COMMITTEES_ACTIVE_PATH, COMMITTEES_ALL_PATH } from "../constants";
import type { CommitteeCatalogTab } from "../types";
import CommitteeCatalogSection from "./CommitteeCatalogSection";

const COMMITTEE_TABS: { label: string; link: Path; tab: CommitteeCatalogTab }[] = [
  { label: "Active Committees", link: COMMITTEES_ACTIVE_PATH, tab: "active" },
  { label: "All Committees", link: COMMITTEES_ALL_PATH, tab: "all" }
];

const COMMITTEE_TAB_CONTENT: Record<CommitteeCatalogTab, { heading: string; body: string }> = {
  active: {
    heading: "Active Committees",
    body: "View active national committees and their participation counts."
  },
  all: {
    heading: "All Committees",
    body: "View national committees with active, inactive, and total participation counts."
  }
};

function committeeTabLink(link: Path, searchParamsUrl: string) {
  const searchParams = new URLSearchParams(searchParamsUrl);
  searchParams.delete("after");
  searchParams.delete("before");
  searchParams.delete("currentPage");
  if (link.endsWith("/active")) {
    searchParams.delete("highlightRow");
  }

  const search = searchParams.toString();
  return `${link}${search ? `?${search}` : ""}` as Path;
}

export function CommitteeManagementPageContent({ activeTab }: { activeTab: CommitteeCatalogTab }) {
  usePageTitle(`/people/membership-services/committees/${activeTab}`, "People | Committee Management");
  const flags = useFlags();
  const canViewCommittees = useCanViewCommitteesBool();
  const navLinks = getMembershipServicesNavLinks(flags, { canViewCommittees });
  const { searchParamsUrl } = useParams();

  if (!canViewCommittees) {
    return <Navigate to="/people/membership-services" replace />;
  }

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="Committee Management"
        backLink={{ to: "/people/membership-services" }}
        compact
      />
      <PageHeading title="Committee Management" compact />
      <Tabs
        tabItems={COMMITTEE_TABS.map((tab) => ({
          label: tab.label,
          link: committeeTabLink(tab.link, searchParamsUrl),
          active: tab.tab === activeTab
        }))}
      />
      <div className="w-full">
        <CommitteeCatalogSection
          catalogTab={activeTab}
          heading={COMMITTEE_TAB_CONTENT[activeTab].heading}
          body={COMMITTEE_TAB_CONTENT[activeTab].body}
        />
      </div>
    </AppLayout>
  );
}
